import React, { FC, useEffect, useMemo } from 'react';

import { useApolloClient, useMutation } from '@apollo/client';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  VStack,
  useUpdateEffect,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import {
  createShippingAddressSchema,
  US_COUNTRY_ID,
  TShippingAddressFormData,
} from './schema';
import { countries as _countries, states, useAuth } from '../../../../Auth';
import { createFormElements } from '../../../../Form';
import { addAddressMutation } from '../../../../gql/mutations/addAddressMutation';
import { deleteAddressMutation } from '../../../../gql/mutations/deleteAddressMutation';
import { updateUserDefaultAddressMutation } from '../../../../gql/mutations/updateUserDefaultAddressMutation';
import { EGTMEvents, useGTMDataLayer } from '../../../../hooks';
import { EButtonVariant, EColor } from '../../../../Theme';
import { ModalContent, ModalHeader } from '../../../components';
import { useModalStore } from '../../../stores/modal';
import { EModalContent } from '../../../types/modalContent';

// Helper function to convert camelCase to readable text
const formatFieldName = (field: string): string => {
  return field
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

// Helper function to format error messages
const formatErrorMessage = (message: string, field?: string): string => {
  if (message === '* Required' && field) {
    return `* ${formatFieldName(field)} Required`;
  }
  return message;
};

const countries = _countries.map((item) => ({
  label: item.label,
  value: item.value,
}));

const { Form, FormTextInput, FormSelect } =
  createFormElements<TShippingAddressFormData>();

export const ShippingAddressFormV2: FC = () => {
  const auth = useAuth();
  const userName = [auth?.profile?.firstName, auth?.profile?.lastName]
    .filter(Boolean)
    .join(' ');

  const pushGTMData = useGTMDataLayer();

  const client = useApolloClient();
  const [createAddress] = useMutation(addAddressMutation);
  const [deleteAddress] = useMutation(deleteAddressMutation);
  const [updateUserDefaultAddress] = useMutation(
    updateUserDefaultAddressMutation
  );

  const modalStore = useModalStore();
  const currentModal = modalStore.lookup();
  const type =
    currentModal?.name === EModalContent.ADD_SHIPPING_ADDRESS_V2
      ? currentModal.type
      : 'create';
  const address =
    currentModal?.name === EModalContent.ADD_SHIPPING_ADDRESS_V2
      ? currentModal.address
      : undefined;
  const isDefault =
    currentModal?.name === EModalContent.ADD_SHIPPING_ADDRESS_V2
      ? currentModal.isDefault
      : undefined;

  const methods = useForm<TShippingAddressFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(createShippingAddressSchema(type)),
    defaultValues: {
      name: userName || '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      phone: auth?.profile?.phone || '',
      ...(type === 'update' && { id: '' }),
    },
  });
  const { control, setValue, setError, reset, formState } = methods;
  const { errors, isSubmitting } = formState;

  const handleBack = () => {
    // Reset form to default values
    reset({
      name: userName || '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      phone: auth?.profile?.phone || '',
      ...(type === 'update' && { id: '' }),
    });
    modalStore.pop();
  };

  const values = useWatch({
    control,
  });
  const { name, line1, city, postalCode, country } = values;

  const isSubmitButtonDisabled = useMemo(
    () => !name || !line1 || !city || !postalCode || !country,

    [city, country, name, line1, postalCode]
  );

  const handleSubmit = async (data: TShippingAddressFormData) => {
    try {
      if (type === 'create') {
        // Create new address
        const result = await createAddress({
          variables: {
            input: {
              name: data.name,
              line1: data.line1,
              line2: data.line2,
              city: data.city,
              state: data.state,
              country: data.country,
              postalCode: data.postalCode,
              phone: data.phone,
            },
          },
        });

        if (result.data?.addressCreateV2?.successful) {
          await client.refetchQueries({
            include: ['webGetUnifiedUserAddressesQuery'],
          });

          handleBack();
        } else {
          // Handle field-specific errors
          const messages = result.data?.addressCreateV2?.messages || [];
          let hasFieldError = false;

          messages.forEach((message) => {
            if (message?.message) {
              if (message?.field) {
                const fieldName =
                  message.field as keyof TShippingAddressFormData;
                const formattedMessage = formatErrorMessage(
                  message.message,
                  message.field
                );
                setError(fieldName, { message: formattedMessage });
                hasFieldError = true;
              } else {
                // If no field specified, treat as root error
                setError('root', {
                  message: message.message,
                });
                hasFieldError = true;
              }
            }
          });

          // If no field-specific errors, show general error
          if (!hasFieldError) {
            setError('root', {
              message: messages[0]?.message ?? "Couldn't add shipping address",
            });
          }

          pushGTMData({
            event: EGTMEvents.ADD_ADDRESS_ERROR,
          });
        }
      } else {
        // Update mode - create new address first, then delete original. If the original address was default, set the new address as default.
        try {
          // First, create the new address
          const result = await createAddress({
            variables: {
              input: {
                name: data.name,
                line1: data.line1,
                line2: data.line2,
                city: data.city,
                state: data.state,
                country: data.country,
                postalCode: data.postalCode,
                phone: data.phone,
              },
            },
          });

          if (!result.data?.addressCreateV2?.successful) {
            // Handle field-specific errors
            const messages = result.data?.addressCreateV2?.messages || [];

            messages.forEach((message: any) => {
              if (message?.message) {
                if (message?.field) {
                  const fieldName =
                    message.field as keyof TShippingAddressFormData;
                  // Check if the field exists in our form data
                  if (fieldName in data) {
                    const formattedMessage = formatErrorMessage(
                      message.message,
                      message.field
                    );
                    setError(fieldName, { message: formattedMessage });
                  } else {
                    // If field doesn't exist in our form, treat as root error
                    setError('root', {
                      message: message.message,
                    });
                  }
                } else {
                  // If no field specified, treat as root error
                  setError('root', {
                    message: message.message,
                  });
                }
              }
            });

            // If no messages at all, show general error
            if (messages.length === 0) {
              setError('root', {
                message: "Couldn't create new address",
              });
            }

            pushGTMData({
              event: EGTMEvents.ADD_ADDRESS_ERROR,
            });
            return;
          }

          // Get the new address ID from the mutation response
          const newAddressId = result.data?.addressCreateV2?.result?.id;
          const wasDefault = isDefault;

          // If the original address was default, set the new address as default
          if (wasDefault && newAddressId) {
            try {
              const updateDefaultResult = await updateUserDefaultAddress({
                variables: {
                  input: {
                    defaultShippingAddressId: newAddressId,
                  },
                },
              });

              if (!updateDefaultResult.data?.accountUpdate?.successful) {
                // If setting default fails, we can't delete the original default address
                // Delete the newly created address and show error
                await deleteAddress({
                  variables: {
                    input: {
                      id: newAddressId,
                    },
                  },
                });

                setError('root', {
                  message:
                    updateDefaultResult.data?.accountUpdate?.messages?.[0]
                      ?.message ?? "Couldn't set new address as default",
                });

                pushGTMData({
                  event: EGTMEvents.ADD_ADDRESS_ERROR,
                });
                return;
              }
            } catch (err) {
              console.error('Failed to update default address:', err);
              // Delete the newly created address and show error
              await deleteAddress({
                variables: {
                  input: {
                    id: newAddressId,
                  },
                },
              });

              setError('root', {
                message: "Couldn't set new address as default",
              });

              pushGTMData({
                event: EGTMEvents.ADD_ADDRESS_ERROR,
              });
              return;
            }
          }

          // Now delete the original address
          const deleteResult = await deleteAddress({
            variables: {
              input: {
                id: address?.id,
              },
            },
          });

          if (!deleteResult.data?.addressDelete?.successful) {
            // Handle delete errors
            const messages = deleteResult.data?.addressDelete?.messages || [];

            messages.forEach((message: any) => {
              if (message?.message) {
                if (message?.field) {
                  const fieldName =
                    message.field as keyof TShippingAddressFormData;
                  // Check if the field exists in our form data
                  if (fieldName in data) {
                    const formattedMessage = formatErrorMessage(
                      message.message,
                      message.field
                    );
                    setError(fieldName, { message: formattedMessage });
                  } else {
                    // If field doesn't exist in our form, treat as root error
                    setError('root', {
                      message: message.message,
                    });
                  }
                } else {
                  // If no field specified, treat as root error
                  setError('root', {
                    message: message.message,
                  });
                }
              }
            });

            if (messages.length === 0) {
              setError('root', {
                message: "Couldn't delete original address",
              });
            }

            pushGTMData({
              event: EGTMEvents.ADD_ADDRESS_ERROR,
            });
            return;
          }

          // Refetch addresses to update cache
          await client.refetchQueries({
            include: ['webGetUnifiedUserAddressesQuery'],
          });

          handleBack();
        } catch (err) {
          setError('root', {
            message: "Couldn't update shipping address",
          });

          pushGTMData({
            event: EGTMEvents.ADD_ADDRESS_ERROR,
          });
        }
      }
    } catch (error) {
      setError('root', {
        message:
          type === 'create'
            ? "Couldn't add shipping address"
            : "Couldn't update shipping address",
      });

      pushGTMData({
        event: EGTMEvents.ADD_ADDRESS_ERROR,
      });
    }
  };

  const handleEnter = (
    event: React.KeyboardEvent<HTMLInputElement> & React.BaseSyntheticEvent
  ) => {
    if (event.code === 'Enter') {
      const { form } = event.target;
      if (form) {
        const index = [...form].indexOf(event.target);
        form[index + 1].focus();
        event.preventDefault();
      }
    }
  };

  const handleInvalid = () => {
    pushGTMData({
      event: EGTMEvents.ADD_ADDRESS_ERROR,
    });
  };

  useUpdateEffect(() => {
    if (country !== US_COUNTRY_ID) {
      setValue('state', '');
    }
  }, [country, setValue]);

  useEffect(() => {
    pushGTMData({
      event: EGTMEvents.ADD_ADDRESS_VIEW,
    });
  }, [pushGTMData]);

  // Populate form with address data when editing
  useEffect(() => {
    if (address && type === 'update') {
      // Find the country ID that matches the country code
      const countryId =
        countries.find((c) => c.value === address.country)?.value || '';

      setValue('name', address.name || userName || '');
      setValue('line1', address.line1 || '');
      setValue('line2', address.line2 || '');
      setValue('city', address.city || '');
      setValue('state', address.state || '');
      setValue('country', countryId);
      setValue('postalCode', address.postalCode || '');
      setValue('phone', address.phone || auth?.profile?.phone || '');
      if (type === 'update') {
        (setValue as any)('id', address.id || '');
      }
    }
  }, [address, type, userName, auth?.profile?.phone, setValue]);

  return (
    <ModalContent
      header={
        <ModalHeader className="shipping-address" onClick={handleBack}>
          {type === 'create'
            ? 'Add Your Shipping Address'
            : 'Edit Your Shipping Address'}
        </ModalHeader>
      }
    >
      <Form {...methods} onInvalid={handleInvalid} onSubmit={handleSubmit}>
        <VStack spacing={4}>
          <FormTextInput
            isDisabled={!!userName.length}
            name="name"
            placeholder="Name"
            variant="brand"
            onKeyUp={handleEnter}
          />

          <FormSelect
            _placeholder={{
              color: EColor.Neutral25,
            }}
            name="country"
            options={countries}
            placeholder="Country"
            variant="brand"
          />

          <FormTextInput
            name="line1"
            placeholder="Address"
            variant="brand"
            onKeyUp={handleEnter}
          />

          <FormTextInput
            name="line2"
            placeholder="Apt or Suite # (Optional)"
            variant="brand"
            onKeyUp={handleEnter}
          />

          <FormTextInput
            name="city"
            placeholder="City"
            variant="brand"
            onKeyUp={handleEnter}
          />

          <Flex gap={3.5}>
            {country === US_COUNTRY_ID ? (
              <FormSelect
                name={'state' as any}
                options={states}
                placeholder="State"
                variant="brand"
              />
            ) : (
              <FormTextInput
                name="state"
                placeholder="State"
                variant="brand"
                onKeyUp={handleEnter}
              />
            )}

            <FormTextInput
              name="postalCode"
              placeholder="Zip"
              variant="brand"
              onKeyUp={handleEnter}
            />
          </Flex>

          <FormControl isInvalid={!!errors.root?.message}>
            <FormErrorMessage
              fontSize="14px"
              lineHeight="18px"
              textTransform="unset"
            >
              {errors.root?.message}
            </FormErrorMessage>
          </FormControl>

          <Button
            fontFamily="ABC Diatype"
            fontSize="16px"
            fontWeight={700}
            height="54px"
            isDisabled={isSubmitButtonDisabled}
            isLoading={isSubmitting}
            lineHeight="16px"
            type="submit"
            variant={EButtonVariant.BRAND_PRIMARY}
            width="100%"
          >
            {type === 'create' ? 'Confirm address' : 'Update address'}
          </Button>
        </VStack>
      </Form>
    </ModalContent>
  );
};
